import {
  ImportCandidateStatus,
  ProcessImportScanRow,
} from "@via/api/process-git-import.api.tsx";
import { classNames, formatDateString } from "@via/utils/helpers.tsx";
import { JSX, useEffect, useMemo, useState } from "react";

/**
 * The batch picker: a filterable, multi select table over a scan's rows, the wizard's Pick step
 * between Source and Consent. It owns its selection and reports it out as rows; the modal owns
 * the rows, the toggle, and the Continue button beneath it.
 *
 * Row identity is the row's INDEX in `rows`, never `repoPath` plus `revId`: a repository can
 * publish `rev-01` beside `rev-1` of the same process, and one of those can come back
 * `unreadable` while the other is `new`. Indexing by the pair would collapse two distinct rows
 * into one. Because the index is the identity, a re-scan (a brand new `rows` array) always clears
 * whatever was selected: an old index into a new array points at whatever row happens to land
 * there now, not at the row a person actually picked.
 *
 * The `latestOnly` toggle is a pass through to the parent, never a client side filter. The scan's
 * `revisions` field decides how many blobs the server read, so the contract fields on a row
 * (name, hash, inputs, outputs, ...) belong to the one revision that was actually read. Flipping
 * the toggle asks the parent to re-scan, which is what turns "no history behind this row" into
 * an honest look at an older revision, rather than this component pretending to know what an
 * unread revision would have looked like.
 */

const ALL_STATUSES: ImportCandidateStatus[] = [
  "new",
  "update-available",
  "already-imported",
  "uuid-conflict",
  "contract-refused",
  "unreadable",
];

const STATUS_LABEL: Record<ImportCandidateStatus, string> = {
  new: "New",
  "update-available": "Update available",
  "already-imported": "Already imported",
  "uuid-conflict": "UUID conflict",
  "contract-refused": "Contract refused",
  unreadable: "Unreadable",
};

const STATUS_BADGE_CLASS: Record<ImportCandidateStatus, string> = {
  new: "bg-green-50 text-green-700 ring-green-600/20",
  "update-available": "bg-blue-50 text-blue-700 ring-blue-600/20",
  "already-imported": "bg-gray-100 text-gray-600 ring-gray-500/10",
  "uuid-conflict": "bg-amber-50 text-amber-700 ring-amber-600/20",
  "contract-refused": "bg-red-50 text-red-700 ring-red-600/10",
  unreadable: "bg-red-50 text-red-700 ring-red-600/10",
};

/** Only a `new` row is importable. Every other status is already taken, wrong, or unreadable. */
const isSelectableStatus = (status: ImportCandidateStatus): boolean =>
  "new" === status;

type AssetsBucket = "has" | "none";

const assetsBucketOf = (row: ProcessImportScanRow): AssetsBucket =>
  null !== row.assetCount && row.assetCount > 0 ? "has" : "none";

/** `active` with only the values `present` still holds; the same Set when nothing was dropped, so an unchanged filter never re-renders. */
const keptIn = <T,>(active: Set<T>, present: Set<T>): Set<T> => {
  const kept = new Set(Array.from(active).filter(value => present.has(value)));
  return kept.size === active.size ? active : kept;
};

const toggledSet = <T,>(set: Set<T>, value: T): Set<T> => {
  const next = new Set(set);
  if (next.has(value)) {
    next.delete(value);
  } else {
    next.add(value);
  }
  return next;
};

/**
 * Collapses a set of row indices down to at most one per repoPath, keeping the row with the
 * highest revId. Shared by both "select all filtered" and "invert" so the one revision per
 * process rule cannot diverge between the two.
 *
 * Every index handed in is assumed already selectable: a checkbox can never toggle a non
 * selectable row on, so nothing here re-checks status.
 *
 * Deliberately applied to the WHOLE resulting selection, never just the visible or just the
 * filtered subset: a row hidden by the current filter can still hold a higher revision of a
 * process than a row a bulk action is about to add for that same process, and "one revision per
 * process" has to win regardless of which side of the filter each row sits on. When a hidden
 * selected row and a newly offered visible row collide, the higher revId always wins, whichever
 * side it is on. That is what stops a bulk action from silently downgrading a selection to an
 * older revision just because the newer one scrolled out of the current filter, and it is also
 * what stops it from ending up with two revisions of one process, which spec 4.3 never allows.
 */
const dedupeToHighestRevisionPerRepoPath = (
  indices: Set<number>,
  allRows: ProcessImportScanRow[]
): Set<number> => {
  const bestIdxByRepoPath = new Map<string, number>();
  indices.forEach(idx => {
    const row = allRows[idx];
    const currentBestIdx = bestIdxByRepoPath.get(row.repoPath);
    if (
      undefined === currentBestIdx ||
      row.revId > allRows[currentBestIdx].revId
    ) {
      bestIdxByRepoPath.set(row.repoPath, idx);
    }
  });
  return new Set(bestIdxByRepoPath.values());
};

const FacetChip = ({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}): JSX.Element => (
  <button
    type="button"
    aria-pressed={active}
    onClick={onClick}
    className={classNames(
      "rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset",
      active
        ? "bg-indigo-600 text-white ring-indigo-600"
        : "bg-white text-gray-700 ring-gray-300 hover:bg-gray-50"
    )}>
    {label}
  </button>
);

export default function PickTableStep({
  rows,
  maxBatchImport,
  latestOnly,
  onLatestOnlyChange,
  isScanning = false,
  onSelectionChange,
}: {
  rows: ProcessImportScanRow[];
  maxBatchImport: number;
  latestOnly: boolean;
  onLatestOnlyChange: (next: boolean) => void;
  isScanning?: boolean;
  onSelectionChange: (selectedRows: ProcessImportScanRow[]) => void;
}): JSX.Element {
  const [search, setSearch] = useState("");
  const [statusFacet, setStatusFacet] = useState<Set<ImportCandidateStatus>>(
    new Set()
  );
  const [fidelityFacet, setFidelityFacet] = useState<Set<string>>(new Set());
  const [assetsFacet, setAssetsFacet] = useState<Set<AssetsBucket>>(new Set());
  const [selectedIndices, setSelectedIndices] = useState<Set<number>>(
    new Set()
  );

  // A re-scan hands this component a brand new `rows` array. Selection is keyed by index into
  // that array, so the moment the array underneath it changes, a stale index would silently read
  // as whatever row now happens to land there instead of the row a person actually picked.
  // Clearing here, on the array reference itself, is what keeps that from ever happening.
  useEffect(() => {
    setSelectedIndices(new Set());
    onSelectionChange([]);
    // A status or fidelity chip renders only for a value these rows carry, so an active filter
    // the new rows cannot satisfy would keep hiding rows with no chip left on screen to turn it
    // off. Drop exactly those; a filter the new rows can still satisfy stays on. (The two assets
    // chips always render, so that filter can always be turned off.)
    setStatusFacet(prev => keptIn(prev, new Set(rows.map(row => row.status))));
    setFidelityFacet(prev =>
      keptIn(
        prev,
        new Set(
          rows
            .map(row => row.fidelityLevel)
            .filter((level): level is string => null !== level)
        )
      )
    );
    // Intentionally keyed on `rows` alone: `onSelectionChange` is invoked, not depended on, and a
    // fresh identity for it on every parent render must never itself clear a selection nothing
    // about `rows` actually changed.
  }, [rows]);

  const normalizedSearch = search.trim().toLowerCase();

  const passesSearch = (row: ProcessImportScanRow): boolean => {
    if ("" === normalizedSearch) {
      return true;
    }
    return [row.repoPath, row.name, row.summary].some(
      field => null !== field && field.toLowerCase().includes(normalizedSearch)
    );
  };

  const passesStatusFacet = (row: ProcessImportScanRow): boolean =>
    0 === statusFacet.size || statusFacet.has(row.status);

  const passesFidelityFacet = (row: ProcessImportScanRow): boolean =>
    0 === fidelityFacet.size ||
    (null !== row.fidelityLevel && fidelityFacet.has(row.fidelityLevel));

  const passesAssetsFacet = (row: ProcessImportScanRow): boolean =>
    0 === assetsFacet.size || assetsFacet.has(assetsBucketOf(row));

  // AND across the three facet groups, OR within one: a group with nothing chosen matches every
  // row (it imposes no constraint), and a group with chips chosen matches a row satisfying any of
  // them.
  const filteredRows = useMemo(
    () =>
      rows
        .map((row, idx) => ({ idx, row }))
        .filter(
          ({ row }) =>
            passesSearch(row) &&
            passesStatusFacet(row) &&
            passesFidelityFacet(row) &&
            passesAssetsFacet(row)
        ),
    // Deps list the primitives that drive the closures above (`passesSearch` and friends), not
    // the closures themselves: each is redefined every render from these same primitives, so
    // depending on the primitives directly is what actually decides when to recompute.
    [rows, normalizedSearch, statusFacet, fidelityFacet, assetsFacet]
  );

  const statusOptions = useMemo(
    () =>
      ALL_STATUSES.filter(status => rows.some(row => status === row.status)),
    [rows]
  );

  const fidelityOptions = useMemo(
    () =>
      Array.from(
        new Set(
          rows
            .map(row => row.fidelityLevel)
            .filter((level): level is string => null !== level)
        )
      ).sort(),
    [rows]
  );

  const selectedCount = selectedIndices.size;

  const applySelection = (next: Set<number>): void => {
    setSelectedIndices(next);
    onSelectionChange(rows.filter((_, idx) => next.has(idx)));
  };

  const toggleRow = (idx: number): void => {
    applySelection(toggledSet(selectedIndices, idx));
  };

  const handleSelectAllFiltered = (): void => {
    // The best filtered candidate per repoPath is only half of what "select all" has to weigh: a
    // repoPath's current best row can be hidden by the filter right now (a higher revision the
    // user already selected before narrowing the filter). Building the candidate set as the
    // union of the EXISTING selection and each filtered group's own best visible row, then
    // deduping the WHOLE thing through the shared helper, is what lets a hidden higher selection
    // beat a merely visible lower one instead of losing to it just because it is on screen.
    const bestFilteredIdxByRepoPath = new Map<string, number>();
    filteredRows.forEach(({ idx, row }) => {
      if (!isSelectableStatus(row.status)) {
        return;
      }
      const currentBestIdx = bestFilteredIdxByRepoPath.get(row.repoPath);
      if (
        undefined === currentBestIdx ||
        row.revId > rows[currentBestIdx].revId
      ) {
        bestFilteredIdxByRepoPath.set(row.repoPath, idx);
      }
    });
    const candidates = new Set(selectedIndices);
    bestFilteredIdxByRepoPath.forEach(idx => candidates.add(idx));
    const deduped = dedupeToHighestRevisionPerRepoPath(candidates, rows);

    // Capping never drops a row the user already had selected: only the NEWLY added rows (those
    // in `deduped` but not already in `selectedIndices`) are rationed against the remaining
    // capacity, in row order, so a bulk click can add up to the cap without ever silently kicking
    // out something selected before it ran.
    const next = new Set(
      Array.from(deduped).filter(idx => selectedIndices.has(idx))
    );
    const newlyAdded = Array.from(deduped)
      .filter(idx => !selectedIndices.has(idx))
      .sort((a, b) => a - b);
    for (const idx of newlyAdded) {
      if (next.size >= maxBatchImport) {
        break;
      }
      next.add(idx);
    }
    applySelection(next);
  };

  const handleSelectNone = (): void => {
    applySelection(new Set());
  };

  const handleInvert = (): void => {
    const next = new Set(selectedIndices);
    filteredRows.forEach(({ idx, row }) => {
      if (!isSelectableStatus(row.status)) {
        return;
      }
      if (next.has(idx)) {
        next.delete(idx);
      } else {
        next.add(idx);
      }
    });
    // Inverting can put two revisions of one process into `next` at once (each was independently
    // toggled), which the one revision per process rule never allows. The shared helper keeps the
    // highest revision per repoPath across the whole post-toggle set, then this trims to the cap
    // the same way selection always does: by row order, so the result is deterministic rather
    // than Set iteration order.
    const deduped = dedupeToHighestRevisionPerRepoPath(next, rows);
    const capped = Array.from(deduped).sort((a, b) => a - b);
    applySelection(new Set(capped.slice(0, maxBatchImport)));
  };

  const isSiblingSelected = (idx: number, row: ProcessImportScanRow): boolean =>
    Array.from(selectedIndices).some(
      otherIdx => otherIdx !== idx && rows[otherIdx].repoPath === row.repoPath
    );

  if (0 === rows.length) {
    return (
      <p className="text-sm text-gray-500">
        The scan did not find any process revisions to import.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-[220px] flex-1">
          <input
            type="search"
            aria-label="Search processes"
            value={search}
            onChange={event => setSearch(event.target.value)}
            placeholder="Search by name, slug or summary"
            className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            aria-label="Latest revision only"
            checked={latestOnly}
            disabled={isScanning}
            onChange={event => onLatestOnlyChange(event.target.checked)}
          />
          Latest revision only
        </label>
        {/* Flipping the toggle re-scans the repository, which can take a while. Greying the
            toggle alone does not say anything is happening. */}
        {isScanning ? (
          <p
            role="status"
            className="text-sm text-gray-500"
            data-testid="importPickScanning">
            Scanning the repository...
          </p>
        ) : null}
      </div>

      <div className="flex flex-wrap gap-4">
        {statusOptions.length > 0 ? (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-xs font-medium text-gray-500">Status</span>
            {statusOptions.map(status => (
              <FacetChip
                key={status}
                label={STATUS_LABEL[status]}
                active={statusFacet.has(status)}
                onClick={() => setStatusFacet(prev => toggledSet(prev, status))}
              />
            ))}
          </div>
        ) : null}
        {fidelityOptions.length > 0 ? (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-xs font-medium text-gray-500">Fidelity</span>
            {fidelityOptions.map(level => (
              <FacetChip
                key={level}
                label={level}
                active={fidelityFacet.has(level)}
                onClick={() =>
                  setFidelityFacet(prev => toggledSet(prev, level))
                }
              />
            ))}
          </div>
        ) : null}
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs font-medium text-gray-500">Assets</span>
          <FacetChip
            label="Has assets"
            active={assetsFacet.has("has")}
            onClick={() => setAssetsFacet(prev => toggledSet(prev, "has"))}
          />
          <FacetChip
            label="No assets"
            active={assetsFacet.has("none")}
            onClick={() => setAssetsFacet(prev => toggledSet(prev, "none"))}
          />
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleSelectAllFiltered}
            className="rounded border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50">
            Select all
          </button>
          <button
            type="button"
            onClick={handleSelectNone}
            className="rounded border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50">
            Select none
          </button>
          <button
            type="button"
            onClick={handleInvert}
            className="rounded border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50">
            Invert
          </button>
        </div>
        <p className="text-sm font-medium text-gray-700">
          {selectedCount} of {maxBatchImport} selected
        </p>
      </div>

      <div className="overflow-x-auto rounded-md border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left font-medium text-gray-500">
                <span className="sr-only">Select</span>
              </th>
              <th className="px-3 py-2 text-left font-medium text-gray-500">
                Name
              </th>
              <th className="px-3 py-2 text-left font-medium text-gray-500">
                Summary
              </th>
              <th className="px-3 py-2 text-left font-medium text-gray-500">
                Revision
              </th>
              <th className="px-3 py-2 text-left font-medium text-gray-500">
                Inputs / outputs
              </th>
              <th className="px-3 py-2 text-left font-medium text-gray-500">
                Fidelity
              </th>
              <th className="px-3 py-2 text-left font-medium text-gray-500">
                Assets
              </th>
              <th className="px-3 py-2 text-left font-medium text-gray-500">
                Committed
              </th>
              <th className="px-3 py-2 text-left font-medium text-gray-500">
                Status
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {0 === filteredRows.length ? (
              <tr>
                <td
                  colSpan={9}
                  className="px-3 py-4 text-center text-sm text-gray-500">
                  No process revisions match these filters.
                </td>
              </tr>
            ) : (
              filteredRows.map(({ idx, row }) => {
                const isSelected = selectedIndices.has(idx);
                const selectableStatus = isSelectableStatus(row.status);
                const siblingSelected =
                  selectableStatus &&
                  !isSelected &&
                  isSiblingSelected(idx, row);
                const capReached =
                  selectableStatus &&
                  !isSelected &&
                  !siblingSelected &&
                  selectedCount >= maxBatchImport;
                const disabled =
                  !selectableStatus || siblingSelected || capReached;
                const extraDisabledReason = siblingSelected
                  ? "another revision of this process is selected"
                  : capReached
                    ? `the batch cap of ${maxBatchImport} has been reached`
                    : null;

                return (
                  <tr key={idx} className={disabled ? "bg-gray-50" : undefined}>
                    <td className="px-3 py-2 align-top">
                      <input
                        type="checkbox"
                        aria-label={`${row.repoPath} revision ${row.revId}`}
                        checked={isSelected}
                        disabled={disabled}
                        onChange={() => toggleRow(idx)}
                      />
                      {selectableStatus && null !== extraDisabledReason ? (
                        <p className="mt-1 text-xs text-gray-500">
                          {extraDisabledReason}
                        </p>
                      ) : null}
                    </td>
                    <td className="px-3 py-2 align-top">
                      {null !== row.name ? (
                        <>
                          <p className="font-medium text-gray-900">
                            {row.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {row.repoPath}
                          </p>
                        </>
                      ) : (
                        <p className="font-medium text-gray-900">
                          {row.repoPath}
                        </p>
                      )}
                    </td>
                    <td className="px-3 py-2 align-top text-gray-700">
                      {row.summary ?? "-"}
                    </td>
                    <td className="px-3 py-2 align-top text-gray-700">
                      Revision {row.revId}
                      {row.revisions.length > 1 ? (
                        <span className="ml-1 text-xs text-gray-500">
                          ({row.revisions.length} revisions)
                        </span>
                      ) : null}
                    </td>
                    <td className="px-3 py-2 align-top text-gray-700">
                      {row.inputCount ?? "-"} in / {row.outputCount ?? "-"} out
                    </td>
                    <td className="px-3 py-2 align-top text-gray-700">
                      {row.fidelityLevel ?? "-"}
                    </td>
                    <td className="px-3 py-2 align-top text-gray-700">
                      {row.assetCount ?? "-"}
                    </td>
                    <td className="px-3 py-2 align-top text-gray-500">
                      {null !== row.committedAt
                        ? formatDateString(row.committedAt, "LL")
                        : "-"}
                    </td>
                    <td className="px-3 py-2 align-top">
                      <span
                        className={classNames(
                          "inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ring-1 ring-inset",
                          STATUS_BADGE_CLASS[row.status]
                        )}>
                        {STATUS_LABEL[row.status]}
                      </span>
                      {null !== row.statusReason ? (
                        <p className="mt-1 text-xs text-gray-500">
                          {row.statusReason}
                        </p>
                      ) : null}
                      {null !== row.existingProcessId ? (
                        <p className="mt-1 text-xs text-gray-500">
                          Process #{row.existingProcessId}
                        </p>
                      ) : null}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
